import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { type LeadSubmission } from "interfaces/lead-submission";
import {
  createLeadSubmission,
  getLeadSubmissionById,
} from "services/SauceService";
import {
  buildSelfCampaignSearchWithId,
  getSelfCampaignQueryParams,
} from "./queryParams";

const BASE_PATH = "https://www.mustard.love";
const PRICING_REDIRECT_PATH = `${BASE_PATH}/pricing`;
const WELCOME_REDIRECT_PATH = `${BASE_PATH}/welcome`;

type UseSelfCampaignLeadMode = "create" | "required";

type UseSelfCampaignLeadOptions = {
  mode: UseSelfCampaignLeadMode;
};

type UseSelfCampaignLeadResult = {
  leadSubmission: LeadSubmission | null;
  leadSubmissionId: string | null;
  loadingLeadSubmission: boolean;
  queryParams: ReturnType<typeof getSelfCampaignQueryParams>;
};

const asNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

export const selfCampaignLeadNavigation = {
  redirect(path: string): void {
    window.location.assign(path);
  },
};

const hasBootstrapParams = (params: {
  email?: string;
  businessName?: string;
  phone?: string;
  partner?: string;
}): boolean => {
  return Boolean(params.email && params.businessName && params.phone);
};

export const useSelfCampaignLead = ({
  mode,
}: UseSelfCampaignLeadOptions): UseSelfCampaignLeadResult => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(() => {
    return getSelfCampaignQueryParams(location.search);
  }, [location.search]);
  const { id, legacyLeadSubmissionId, businessName, email, phone, partner } =
    queryParams;

  const [leadSubmission, setLeadSubmission] = useState<LeadSubmission | null>(
    null,
  );
  const [loadingLeadSubmission, setLoadingLeadSubmission] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const resolveLeadSubmission = async () => {
      setLoadingLeadSubmission(true);
      setLeadSubmission(null);

      if (id && legacyLeadSubmissionId) {
        navigate(
          {
            pathname: location.pathname,
            search: buildSelfCampaignSearchWithId(location.search, id),
          },
          { replace: true },
        );
        return;
      }

      let nextLeadSubmissionId = id;
      if (!nextLeadSubmissionId) {
        if (mode !== "create") {
          selfCampaignLeadNavigation.redirect(WELCOME_REDIRECT_PATH);
          return;
        }

        if (!hasBootstrapParams({ email, businessName, phone })) {
          selfCampaignLeadNavigation.redirect(WELCOME_REDIRECT_PATH);
          return;
        }

        try {
          const createdLeadSubmission = await createLeadSubmission({
            businessName: businessName as string,
            businessEmail: email as string,
            businessPhone: phone as string,
            partner: partner as string,
          });
          const createdLeadSubmissionId =
            asNonEmptyString(createdLeadSubmission?.id) ||
            asNonEmptyString(createdLeadSubmission?._id) ||
            asNonEmptyString(createdLeadSubmission?.leadSubmissionId);

          if (!createdLeadSubmissionId) {
            selfCampaignLeadNavigation.redirect(PRICING_REDIRECT_PATH);
            return;
          }

          navigate(
            {
              pathname: location.pathname,
              search: buildSelfCampaignSearchWithId(
                location.search,
                createdLeadSubmissionId,
              ),
            },
            { replace: true },
          );
          return;
        } catch (_error) {
          selfCampaignLeadNavigation.redirect(PRICING_REDIRECT_PATH);
          return;
        }
      }

      try {
        const response = await getLeadSubmissionById(nextLeadSubmissionId);

        if (!isMounted) {
          return;
        }

        setLeadSubmission(response);
        setLoadingLeadSubmission(false);
      } catch (_error) {
        selfCampaignLeadNavigation.redirect(WELCOME_REDIRECT_PATH);
      }
    };

    void resolveLeadSubmission();

    return () => {
      isMounted = false;
    };
  }, [
    location.pathname,
    location.search,
    mode,
    navigate,
    businessName,
    email,
    id,
    legacyLeadSubmissionId,
    partner,
    phone,
  ]);

  return {
    leadSubmission,
    leadSubmissionId: queryParams.id || null,
    loadingLeadSubmission,
    queryParams,
  };
};
